import { Badge, Box, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { FieldMapValue, MdmDataReviewedItem, MdmProblemsAddressed, MdmRisk } from '@hb/shared'

const problemsLabel: Record<MdmProblemsAddressed, string> = {
  'self-limited': 'Self-limited or minor problem',
  'stable-chronic': 'Stable chronic illness',
  'undiagnosed-new': 'Undiagnosed new problem requiring additional work-up',
  'acute-systemic': 'Acute illness with systemic symptoms',
  'chronic-severe': 'Chronic illness with severe exacerbation',
  'threat-to-life': 'Acute or chronic illness posing threat to life or function',
}

const riskColorScheme: Record<MdmRisk, string> = {
  minimal: 'green',
  low: 'blue',
  moderate: 'orange',
  high: 'red',
}

const riskLabel: Record<MdmRisk, string> = {
  minimal: 'Minimal',
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
}

const dataLabel: Record<MdmDataReviewedItem, string> = {
  'labs-reviewed': 'Lab results',
  'external-records': 'External records',
  'independent-test-interpretation': 'Independent test interpretation',
  'provider-consultation': 'Provider consultation',
  'independent-historian': 'Independent historian',
}

const LabelText = ({ label }: { label: string }) => (
  <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={0.5}>
    {label}
  </Text>
)

export const MdmView = ({ data }: { data: FieldMapValue }) => {
  const problems = data?.mdmProblemsAddressed as MdmProblemsAddressed | undefined
  const dataReviewed = data?.mdmDataReviewed as MdmDataReviewedItem[] | undefined
  const risk = data?.mdmRiskOfManagement as MdmRisk | undefined
  const summary = data?.mdmSummary as string | undefined
  // mdmComplexity intentionally not shown per §2.10.1 spec

  return (
    <VStack align="flex-start" spacing={3} w="100%">
      {problems && (
        <Box>
          <LabelText label="Problems Addressed" />
          <Text fontSize="sm">{problemsLabel[problems]}</Text>
        </Box>
      )}
      {dataReviewed && dataReviewed.length > 0 && (
        <Box>
          <LabelText label="Data Reviewed" />
          <Wrap spacing={1}>
            {dataReviewed.map(item => (
              <WrapItem key={item}>
                <Badge colorScheme="blue" variant="outline" fontSize="xs">
                  {dataLabel[item]}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}
      {risk && (
        <Box>
          <LabelText label="Risk of Management" />
          <Badge colorScheme={riskColorScheme[risk]} fontSize="sm" px={2} py={0.5}>
            {riskLabel[risk]}
          </Badge>
        </Box>
      )}
      {summary && (
        <Box>
          <LabelText label="Clinical Summary" />
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {summary}
          </Text>
        </Box>
      )}
    </VStack>
  )
}
